import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Clock, MapPin, LogIn, LogOut, X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import CheckInOutModal from './CheckInOutModal';
import {
  initFreshnessSnapshot,
  wasFreshnessReloadJustPerformed,
  readAndClearPendingAttendanceAction,
} from '../utils/runtimeFreshness';
import { resolveShiftInstant } from '../utils/shiftDateTime';

/**
 * ClockInReminder
 *
 * Polls the talent's bookings every 60 seconds and surfaces a *very*
 * obvious reminder banner when:
 *   - Clock-in window: 30 min before start  →  start + 30 min, AND no clock-in yet
 *   - Clock-out window: 15 min before end   →  end + 60 min, AND clocked-in but no clock-out
 *
 * The banner has one large, high-contrast button that opens the check-in
 * modal directly. If the talent dismisses it, we keep a smaller sticky
 * indicator at the top of the dashboard until they actually clock in/out.
 *
 * Goal: even non-technical staff should immediately understand how to
 * clock in/out without searching.
 */

const POLL_INTERVAL_MS = 60_000;
const CLOCK_IN_BEFORE_MIN = 30;
const CLOCK_IN_AFTER_MIN = 30;
const CLOCK_OUT_BEFORE_MIN = 15;
const CLOCK_OUT_AFTER_MIN = 60;

const minutesBetween = (now, then) => Math.round((then.getTime() - now.getTime()) / 60000);

const parseShiftDateTime = (booking, key) => {
  // Timezone-authoritative: resolve start/end in the EVENT'S timezone (or a
  // backend-provided UTC instant / booking-level override), never the viewer's
  // device timezone. Prevents the clock-in window from drifting by the tz
  // offset (the Atlanta 10:00 AM incident).
  const which = key === 'end_time' ? 'end' : 'start';
  return resolveShiftInstant(booking, which);
};

const ClockInReminder = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());
  const [dismissedBookingIds, setDismissedBookingIds] = useState(() => {
    try {
      return new Set(JSON.parse(sessionStorage.getItem('clockReminderDismissed') || '[]'));
    } catch {
      return new Set();
    }
  });
  const [activeModal, setActiveModal] = useState(null); // {booking, type}

  const fetchBookings = useCallback(async () => {
    try {
      const res = await api.get('/talent/bookings');
      setBookings(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      // silent failure — reminder is best-effort
      console.error('ClockInReminder fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
    const tick = setInterval(() => setNow(new Date()), 30_000);
    const poll = setInterval(fetchBookings, POLL_INTERVAL_MS);
    // Iter-411o — Take a runtime freshness snapshot on mount so future
    // check-in/out modals have a baseline to compare against.
    initFreshnessSnapshot();
    return () => { clearInterval(tick); clearInterval(poll); };
  }, [fetchBookings]);

  // Iter-411o — After a freshness-triggered reload, automatically
  // reopen the attendance modal the user was mid-flow on so they
  // don't lose their place onsite.
  useEffect(() => {
    if (!wasFreshnessReloadJustPerformed()) return;
    const pending = readAndClearPendingAttendanceAction();
    if (!pending || !pending.bookingId) return;
    // Wait for bookings to arrive so we can pair the booking object.
    let cancelled = false;
    const tryOpen = () => {
      if (cancelled) return;
      const bk = bookings.find((b) => String(b.id) === String(pending.bookingId));
      if (bk) {
        setActiveModal({ booking: bk, type: pending.type });
        toast.success('Reopened your check-in with the latest attendance tools.');
        return;
      }
      setTimeout(tryOpen, 500);
    };
    tryOpen();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings]);

  const dismiss = (bookingId) => {
    const next = new Set(dismissedBookingIds);
    next.add(bookingId);
    setDismissedBookingIds(next);
    try {
      sessionStorage.setItem('clockReminderDismissed', JSON.stringify(Array.from(next)));
    } catch { /* ignore */ }
  };

  // Find first eligible booking for clock-in or clock-out.
  // Clock-out reminders take priority — they're more time-sensitive (overtime risk).
  const reminder = (() => {
    for (const b of bookings) {
      if (b.status === 'cancelled') continue;
      const start = parseShiftDateTime(b, 'start_time');
      const end = parseShiftDateTime(b, 'end_time');

      // Clock-out window
      if (b.checkin_time && !b.checkout_time && end) {
        const minsToEnd = minutesBetween(now, end);
        if (minsToEnd <= CLOCK_OUT_BEFORE_MIN && minsToEnd >= -CLOCK_OUT_AFTER_MIN) {
          return { booking: b, type: 'checkout', minutes: minsToEnd, start, end };
        }
      }

      // Clock-in window
      if (!b.checkin_time && start) {
        const minsToStart = minutesBetween(now, start);
        if (minsToStart <= CLOCK_IN_BEFORE_MIN && minsToStart >= -CLOCK_IN_AFTER_MIN) {
          return { booking: b, type: 'checkin', minutes: minsToStart, start, end };
        }
      }
    }
    return null;
  })();

  if (loading || !reminder) {
    // No active reminder — clear the cross-component flag so other modals
    // (e.g. WelcomeVideo) can show normally.
    try { sessionStorage.removeItem('clockReminderActive'); } catch { /* ignore */ }
    return null;
  }

  const { booking, type, minutes } = reminder;
  const isCheckin = type === 'checkin';
  const dismissed = dismissedBookingIds.has(booking.id);
  // Cross-component flag so WelcomeVideo and other auto-open modals
  // suppress themselves while a clock reminder is active.
  try { sessionStorage.setItem('clockReminderActive', '1'); } catch { /* ignore */ }

  const venueLabel = booking.venue_name || `${booking.city || ''}${booking.state ? ', ' + booking.state : ''}`.trim() || 'your shift';
  const ActionIcon = isCheckin ? LogIn : LogOut;
  const buttonLabel = isCheckin ? 'Clock In Now' : 'Clock Out Now';
  const headlineText = isCheckin
    ? `It's time to clock in for your shift at ${venueLabel}`
    : `It's time to clock out from ${venueLabel}`;
  const subText = isCheckin
    ? (minutes >= 0
        ? `Starts in ${minutes} minute${minutes === 1 ? '' : 's'}`
        : `Started ${Math.abs(minutes)} minute${Math.abs(minutes) === 1 ? '' : 's'} ago`)
    : (minutes >= 0
        ? `Ends in ${minutes} minute${minutes === 1 ? '' : 's'}`
        : `Ended ${Math.abs(minutes)} minute${Math.abs(minutes) === 1 ? '' : 's'} ago`);

  const openModal = () => {
    if (!booking.venue_lat || !booking.venue_lng) {
      toast.warning('This shift has no venue coordinates set. Your check-in will be flagged for review.');
    }
    setActiveModal({ booking, type });
  };

  const handleSuccess = () => {
    setActiveModal(null);
    // Refresh bookings so we stop showing the reminder.
    fetchBookings();
    // Clear dismissal in case talent dismissed earlier
    const next = new Set(dismissedBookingIds);
    next.delete(booking.id);
    setDismissedBookingIds(next);
    try {
      sessionStorage.setItem('clockReminderDismissed', JSON.stringify(Array.from(next)));
    } catch { /* ignore */ }
  };

  return (
    <>
      {/* Full reminder banner */}
      {!dismissed && (
        <Card
          className={`mb-4 border-2 ${isCheckin ? 'border-cyan-500 bg-cyan-50' : 'border-amber-500 bg-amber-50'} shadow-lg`}
          data-testid={`clock-${type}-reminder`}
        >
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className={`shrink-0 rounded-full p-2 ${isCheckin ? 'bg-cyan-600' : 'bg-amber-600'} text-white`}>
                  <Clock className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-bold text-base sm:text-lg text-gray-900 break-words" data-testid={`clock-${type}-headline`}>
                    {headlineText}
                  </h3>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-700">
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" />
                      {booking.role_name}
                    </span>
                    <span aria-hidden="true">•</span>
                    <span>{subText}</span>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => dismiss(booking.id)}
                className="shrink-0 p-1 rounded-md text-gray-500 hover:bg-gray-200 hover:text-gray-700"
                aria-label="Dismiss reminder"
                data-testid={`clock-${type}-dismiss`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <Button
              onClick={openModal}
              size="lg"
              className={`mt-4 w-full h-14 text-lg font-bold shadow-md ${
                isCheckin
                  ? 'bg-cyan-600 hover:bg-cyan-700 text-white'
                  : 'bg-amber-600 hover:bg-amber-700 text-white'
              }`}
              data-testid={`clock-${type}-now-btn`}
            >
              <ActionIcon className="w-5 h-5 mr-2" />
              {buttonLabel}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Sticky compact reminder if dismissed but still in window */}
      {dismissed && (
        <button
          type="button"
          onClick={openModal}
          className={`mb-4 w-full flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm font-medium border ${
            isCheckin
              ? 'border-cyan-300 bg-cyan-50 text-cyan-900 hover:bg-cyan-100'
              : 'border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100'
          }`}
          data-testid={`clock-${type}-mini-reminder`}
        >
          <span className="inline-flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {isCheckin ? 'Clock in for' : 'Clock out from'} {venueLabel}
          </span>
          <span className="inline-flex items-center gap-1 font-semibold">
            <ActionIcon className="w-4 h-4" /> {buttonLabel}
          </span>
        </button>
      )}

      {activeModal && (
        <CheckInOutModal
          open={!!activeModal}
          booking={{
            ...activeModal.booking,
            // CheckInOutModal expects venue_lat/venue_lng/gps_radius_meters/role_name on top-level.
            venue_lat: activeModal.booking.venue_lat,
            venue_lng: activeModal.booking.venue_lng,
            gps_radius_meters: activeModal.booking.gps_radius_meters || 500,
          }}
          type={activeModal.type}
          onClose={() => setActiveModal(null)}
          onSuccess={handleSuccess}
        />
      )}
    </>
  );
};

export default ClockInReminder;
